<script setup>
import { ref, computed } from 'vue'

const emit = defineEmits(['resetPagination'])

// Search & filters
const searchQuery = ref('')
const roleFilter = ref('')
const clientLinkFilter = ref('')
const filtersExpanded = ref(false)

// Date filter (draft values)
const dateFrom = ref('')
const dateTo = ref('')
const dateMode = ref('')

// Date filter (applied values)
const appliedDateFrom = ref('')
const appliedDateTo = ref('')
const appliedDateMode = ref('')

const isDateFilterActive = computed(() => {
  return appliedDateMode.value && (appliedDateFrom.value || appliedDateTo.value)
})

const activeFiltersCount = computed(() => {
  let count = 0
  if (roleFilter.value) count++
  if (clientLinkFilter.value) count++
  if (isDateFilterActive.value) count++
  return count
})

function toggleFilters() {
  filtersExpanded.value = !filtersExpanded.value
}

function applyDateFilter() {
  appliedDateFrom.value = dateFrom.value
  appliedDateTo.value = dateTo.value
  appliedDateMode.value = dateMode.value
  emit('resetPagination')
}

function resetDateFilter() {
  dateFrom.value = ''
  dateTo.value = ''
  dateMode.value = ''
  appliedDateFrom.value = ''
  appliedDateTo.value = ''
  appliedDateMode.value = ''
  emit('resetPagination')
}

function onSearchInput() {
  emit('resetPagination')
}

function clearSearch() {
  searchQuery.value = ''
  onSearchInput()
}

function onFilterChange() {
  emit('resetPagination')
}

defineExpose({
  searchQuery,
  roleFilter,
  clientLinkFilter,
  appliedDateFrom,
  appliedDateTo,
  appliedDateMode,
  isDateFilterActive,
  activeFiltersCount,
  filtersExpanded,
  toggleFilters,
})
</script>

<template>
  <!-- Collapsible Filters Section -->
  <Transition name="filters-collapse">
    <div v-show="filtersExpanded" class="filters-section">
      <!-- Search & Filters Bar -->
      <div class="filters-bar">
        <div class="search-input-container">
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="חיפוש לפי שם, אימייל, טלפון, סטטוס..."
            @input="onSearchInput"
          />
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="filter-controls">
          <div class="select-wrapper">
            <select v-model="roleFilter" class="filter-select" @change="onFilterChange">
              <option value="">כל התפקידים</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
              <option value="agent">Agent</option>
              <option value="null">ללא תפקיד</option>
            </select>
            <svg
              class="select-chevron"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>

          <div class="select-wrapper">
            <select v-model="clientLinkFilter" class="filter-select" @change="onFilterChange">
              <option value="">כל הקישורים</option>
              <option value="linked">מקושר ללקוח</option>
              <option value="not-linked">לא מקושר</option>
            </select>
            <svg
              class="select-chevron"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Date Filter Bar -->
      <div class="date-filter-bar">
        <span class="date-filter-label">תאריכים</span>
        <div class="date-inputs">
          <div class="date-input-group">
            <label class="date-label">מתאריך</label>
            <input v-model="dateFrom" type="date" class="date-input" />
          </div>
          <div class="date-input-group">
            <label class="date-label">עד תאריך</label>
            <input v-model="dateTo" type="date" class="date-input" />
          </div>
          <div class="select-wrapper">
            <select v-model="dateMode" class="filter-select date-mode-select">
              <option value="">בחר סוג סינון</option>
              <option value="active">פעילים בטווח</option>
              <option value="notActive">לא פעילים בטווח</option>
              <option value="signedIn">התחברו בטווח</option>
              <option value="notSignedIn">לא התחברו בטווח</option>
              <option value="joined">הצטרפו בטווח</option>
            </select>
            <svg
              class="select-chevron"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
        </div>
        <div class="date-actions">
          <button class="btn-filter" :disabled="!dateMode" @click="applyDateFilter">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span>סנן</span>
          </button>
          <button
            class="btn-reset"
            :class="{ active: isDateFilterActive }"
            @click="resetDateFilter"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span>נקה</span>
          </button>
        </div>
        <div v-if="isDateFilterActive" class="date-filter-active-badge">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>סינון פעיל</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.filters-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  flex-shrink: 0;
}

.filters-collapse-enter-active,
.filters-collapse-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.filters-collapse-enter-from,
.filters-collapse-leave-to {
  opacity: 0;
  max-height: 0;
  margin-bottom: 0;
}

.filters-collapse-enter-to,
.filters-collapse-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* Filters Bar */
.filters-bar {
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  flex-wrap: wrap;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.search-input-container {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 32px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all var(--transition-fast);
  background: var(--bg-secondary);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: var(--bg-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-clear {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-full);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-error);
  color: white;
}

.filter-controls {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  flex-wrap: wrap;
}

.select-wrapper {
  position: relative;
}

.filter-select {
  appearance: none;
  padding: 8px 32px 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
  cursor: pointer;
  min-width: 130px;
}

.filter-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.select-chevron {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

/* Date Filter Bar */
.date-filter-bar {
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-end;
  flex-wrap: wrap;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color-light);
}

.date-filter-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  white-space: nowrap;
}

.date-inputs {
  display: flex;
  gap: var(--spacing-sm);
  align-items: flex-end;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-label {
  font-size: 10px;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
}

.date-input {
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
  min-width: 140px;
  transition: all var(--transition-fast);
}

.date-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
}

.date-mode-select {
  min-width: 150px;
}

.date-actions {
  display: flex;
  gap: var(--spacing-xs);
  align-self: flex-end;
}

.btn-filter {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 14px;
  background: var(--gradient-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-filter:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-filter:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-reset {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-reset:hover {
  background: var(--bg-tertiary);
  border-color: var(--text-muted);
}

.btn-reset.active {
  background: rgba(239, 68, 68, 0.1);
  border-color: var(--color-error);
  color: var(--color-error);
}

.date-filter-active-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 4px 10px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: var(--border-radius-full);
  color: var(--color-success);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

@media (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
    padding: var(--spacing-sm);
    gap: var(--spacing-sm);
  }

  .search-input-container {
    max-width: none;
  }

  .filter-controls {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
  }

  .filter-controls .select-wrapper {
    flex: 1;
    min-width: 120px;
  }

  .date-filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
  }

  .date-filter-label {
    display: none;
  }

  .date-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-sm);
  }

  .date-input-group {
    width: 100%;
  }

  .date-input-group .date-label {
    font-size: 9px;
    margin-bottom: 2px;
  }

  .date-input {
    width: 100%;
    padding: 8px;
    font-size: 12px;
  }

  .date-inputs .select-wrapper {
    grid-column: 1 / -1;
    width: 100%;
  }

  .date-mode-select {
    width: 100%;
  }

  .date-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-xs);
  }

  .date-actions button {
    justify-content: center;
  }

  .date-filter-active-badge {
    justify-content: center;
  }
}
</style>
